import type { CSSProperties } from 'react';

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from '@rocco/components/dialog';
import { cn } from '@rocco/utils/cn';

export interface GetMatchedExplainer {
    /** Small grey line above the title. */
    eyebrow: string;
    title: string;
    body: string;
}

interface GetMatchedExplainerModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Continue: on to the brief itself. */
    onContinue: () => void;
    explainer: GetMatchedExplainer;
}

/**
 * Slides up from the foot of the window rather than popping at the centre:
 * the enter and exit keyframes compose with the dialog's own centring, which
 * Tailwind v4 keeps on `translate`, so the offsets are the travel alone —
 * none across, and one-and-a-half heights up from below.
 */
const SLIDE_UP: CSSProperties = {
    '--tw-enter-translate-x': '0',
    '--tw-enter-translate-y': '150%',
    '--tw-exit-translate-x': '0',
    '--tw-exit-translate-y': '150%',
    '--tw-enter-scale': '1',
    '--tw-exit-scale': '1',
    animationDuration: '500ms',
    animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
} as CSSProperties;

/**
 * What Get Matched is, before the brief asks anything (Figma 2354:59728): the
 * rail's plus opens this, and Continue opens the questionnaire. The labelled
 * Get Matched button beside the plus goes straight to the questionnaire, so
 * a reader who already knows the flow is not made to read about it.
 */
export const GetMatchedExplainerModal = ({
    open,
    onOpenChange,
    onContinue,
    explainer,
}: GetMatchedExplainerModalProps) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
            variant="default"
            spacing="none"
            closeButtonVariant="rounded"
            // The design's close: a dark disc, white cross, off the corner.
            closeButtonClassName="top-[1.4375rem] right-[1.375rem] size-[2.625rem] bg-[#181818] text-white shadow-none hover:bg-[#2c2c2c]"
            className={cn(
                'w-[56.75rem] max-w-[95vw] overflow-hidden border-0 bg-white',
                'rounded-[1.5rem] sm:rounded-[1.5rem]',
                'max-md:px-6 max-md:py-12 md:px-[5.3125rem] md:pt-[5.3125rem] md:pb-[5.5rem]',
            )}
            style={SLIDE_UP}
        >
            <p className="m-0 font-sans text-[1.0625rem] leading-[1.22] font-medium tracking-[-0.02em] text-[#767676]">
                {explainer.eyebrow}
            </p>
            <DialogTitle className="m-0 mt-[1.0625rem] max-w-[36rem] font-sans text-[3.5rem] leading-[1.07] font-medium tracking-[-0.035em] text-[#323232] max-md:text-[2.25rem]">
                {explainer.title}
            </DialogTitle>
            <DialogDescription className="m-0 mt-[1.34375rem] max-w-[42.5rem] font-sans text-[1.25rem] leading-[1.5] tracking-[-0.01em] text-[#323232] max-md:mt-6 max-md:text-[1.0625rem]">
                {explainer.body}
            </DialogDescription>
            <button
                type="button"
                onClick={onContinue}
                className="mt-[1.75rem] flex h-[3.375rem] w-[11.625rem] items-center justify-center rounded-full bg-[#181818] font-sans text-[1rem] leading-[1.3] font-medium tracking-[0.02rem] text-white transition-colors hover:bg-[#2c2c2c] max-md:mt-8 max-md:w-full"
            >
                Continue
            </button>
        </DialogContent>
    </Dialog>
);
